/**
 * Dummy MEDDPICC field values for the Gazillion preview workspace.
 * Used as initial values for the MEDDPICC methodology section on the
 * deal drill-down sheet so reps can see what populated qualification
 * data looks like without needing to type it themselves.
 */

import { deals } from "./dummy";

type Deal = (typeof deals)[number];

const FIELDS = [
  "meddpicc_metrics",
  "meddpicc_economic_buyer",
  "meddpicc_decision_criteria",
  "meddpicc_decision_process",
  "meddpicc_paper_process",
  "meddpicc_identify_pain",
  "meddpicc_champion",
  "meddpicc_competition",
] as const;

type MeddpiccField = (typeof FIELDS)[number];

function primaryContact(deal: Deal): string {
  const first = (deal.contacts || "").split(",")[0]?.trim();
  return first && first !== "-" ? first : "the primary stakeholder";
}

function secondaryContact(deal: Deal): string {
  const parts = (deal.contacts || "").split(",").map((s) => s.trim()).filter(Boolean);
  return parts[1] || parts[0] || "the buying committee";
}

function formatAmount(amount: number): string {
  if (!amount) return "TBD";
  return `$${amount.toLocaleString()}`;
}

function buildValues(deal: Deal): Record<MeddpiccField, string> {
  const champion = primaryContact(deal);
  const eb = secondaryContact(deal);
  const amount = formatAmount(deal.amount);
  const company = deal.company;

  return {
    meddpicc_metrics: `Target ROI: 3x within 12 months. ${company} expects to reduce manual workload by ~40% and improve cycle time on the ${deal.name.toLowerCase()} workflow. Deal value ${amount}.`,
    meddpicc_economic_buyer: `${eb} controls budget approval for this initiative. Sign-off required from CFO for anything above $50k.`,
    meddpicc_decision_criteria: `1) Native integrations with existing stack 2) SOC 2 + GDPR compliance 3) Time-to-value under 30 days 4) Transparent per-seat pricing.`,
    meddpicc_decision_process: `Technical evaluation (in progress) → security review → procurement → legal redlines → final sign-off by ${eb}. Target close ${deal.closeDate}.`,
    meddpicc_paper_process: `MSA + DPA required. Procurement uses Ironclad. Net-30 payment terms standard. Legal redlines typically take 2 weeks.`,
    meddpicc_identify_pain: `Current workflow at ${company} is fragmented across spreadsheets and Slack threads, causing dropped handoffs and stalled deals. Leadership has called this out as a top-3 priority for the half.`,
    meddpicc_champion: `${champion} is championing internally. Has run two demos for their team, forwarded ROI deck to ${eb}, and offered to host the security review call.`,
    meddpicc_competition: `Evaluating against an incumbent point solution and one Series B startup. We win on integrations and time-to-value; they're cheaper on a pure list-price basis.`,
  };
}

const cache: Record<string, Record<MeddpiccField, string>> = {};

function valuesFor(dealId: string): Record<MeddpiccField, string> | null {
  if (cache[dealId]) return cache[dealId];
  const deal = deals.find((d) => d.id === dealId);
  if (!deal) return null;
  cache[dealId] = buildValues(deal);
  return cache[dealId];
}

export function getDummyMeddpiccValue(dealId: string, fieldName: string): string {
  const values = valuesFor(dealId);
  if (!values) return "";
  return values[fieldName as MeddpiccField] ?? "";
}